// dashboard.js — V4 Dashboard
// Full implementation: stats row, David alerts bar, client grid with filters

const CURRENT_YEAR = new Date().getFullYear();

export function initDashboard() {
  window.renderDashboardView = renderDashboardView;
}

export async function renderDashboardView() {
  const mount = document.getElementById('dashboard-v4-mount');
  if (!mount) return;

  mount.innerHTML = '<div class="dash-empty" style="padding:30px 0 0">Loading…</div>';

  try {
    const [clients, shows, actions] = await Promise.all([
      fetchJSON('/api/v1/clients'),
      fetchJSON('/api/v1/shows'),
      fetchJSON('/api/advisor/pending-actions').catch(() => []),
    ]);

    const yearShows   = shows.filter(s => parseInt(s.season_year) === CURRENT_YEAR);
    const activeShows = yearShows.filter(s => s.status === 'in_progress' || s.status === 'active');

    // Stats
    const contractSigned  = activeShows.filter(s => s.contract_signed).length;
    const outstanding     = yearShows.filter(s => s.invoiced && !s.paid);
    const outstandingAmt  = outstanding.reduce((sum, s) => sum + (parseInt(s.fee_cents) || 0), 0);
    const outstandingOldestDays = outstanding.length
      ? Math.max(...outstanding.map(s => daysSince(s.invoice_date || s.created_at)))
      : 0;
    const now = Date.now();
    const deliveriesSoon = activeShows.filter(s => {
      const d = s.deliver_by_date || s.due_date;
      if (!d) return false;
      const diff = (new Date(d) - now) / 86400000;
      return diff >= 0 && diff <= 45;
    });
    const nextDelivery = deliveriesSoon
      .map(s => ({ name: s.org_name || clientName(clients, s.client_id), date: s.deliver_by_date || s.due_date }))
      .sort((a, b) => new Date(a.date) - new Date(b.date))[0];

    // Alerts from advisor queue (top 3 real alerts)
    const alerts = (Array.isArray(actions) ? actions : [])
      .filter(a => a.action_type === 'alert')
      .slice(0, 3);

    // Build client cards for current year
    const yearClientIds = new Set(yearShows.map(s => s.client_id));
    const yearClients = clients.filter(c => yearClientIds.has(c.id));

    // Past clients — have show history but no show this year
    const pastClients = clients
      .filter(c => !yearClientIds.has(c.id) && shows.some(s => s.client_id === c.id))
      .sort((a, b) => (a.org_name || a.name || '').localeCompare(b.org_name || b.name || ''));

    // Marching revenue — total contracted fees for marching clients this season
    const marchingClientIds = new Set(
      clients
        .filter(c => c.ensemble_type === 'marching' || c.ensemble_type === 'university_band' || c.ensemble_type === 'drum_corps')
        .map(c => c.id)
    );
    const marchingShows     = yearShows.filter(s => marchingClientIds.has(s.client_id));
    const marchingRevCents  = marchingShows.reduce((sum, s) => sum + (parseInt(s.fee_cents) || 0), 0);
    const marchingPaidCents = marchingShows
      .filter(s => s.paid || s.payment_date || s.paid_at)
      .reduce((sum, s) => sum + (parseInt(s.fee_cents) || 0), 0);

    // Overall season progress — average across all active shows with a percent_complete value
    const showsWithPct = activeShows.filter(s => s.percent_complete != null);
    const overallPct = showsWithPct.length
      ? Math.round(showsWithPct.reduce((sum, s) => sum + (parseInt(s.percent_complete) || 0), 0) / showsWithPct.length)
      : 0;

    mount.innerHTML = renderDashboard({
      yearClients, yearShows, activeShows, clients, shows, pastClients,
      stats: {
        active: activeShows.length,
        contractSigned,
        totalActive: activeShows.length,
        outstandingAmt,
        outstandingCount: outstanding.length,
        outstandingOldest: outstandingOldestDays,
        deliveriesSoon: deliveriesSoon.length,
        nextDelivery,
        overallPct,
        showsWithPct: showsWithPct.length,
        marchingRevCents,
        marchingPaidCents,
      },
      alerts,
    });

    bindFilters(yearClients, yearShows);
    bindPastClientsToggle();
    bindNewClientButtons();

    // Update topbar season tag
    const seasonTag = document.getElementById('topbar-season');
    if (seasonTag) {
      seasonTag.textContent = `${CURRENT_YEAR} Season`;
      seasonTag.style.display = '';
    }

  } catch (err) {
    console.error('[Dashboard] Load error:', err.message);
    mount.innerHTML = `<div class="dash-empty">Error loading dashboard — ${err.message}</div>`;
  }
}

// ── Render ─────────────────────────────────────────────────────────────────

function renderDashboard({ yearClients, yearShows, clients, shows, stats, alerts, pastClients }) {
  return `
    <div class="dash-topbar-row">
      <div class="dash-topbar-left">
        <div class="dash-section-label" style="margin-bottom:0">Active Clients — ${CURRENT_YEAR}</div>
      </div>
      <div class="dash-topbar-actions">
        <button class="btn btn-ghost btn-sm" onclick="window.openNewClient('marching')">+ Marching</button>
        <button class="btn btn-ghost btn-sm" onclick="window.openNewClient('composition')">+ Composition</button>
      </div>
    </div>

    ${renderAlertsBar(alerts)}
    ${renderStatsRow(stats)}
    ${renderSeasonGauge(stats)}
    ${renderClientSection(yearClients, yearShows)}
    ${renderPastClientsSection(pastClients, shows)}
  `;
}

function renderSeasonGauge(stats) {
  if (!stats.showsWithPct) return '';
  const pct = stats.overallPct || 0;
  // Gradient: deep red → amber → green (same as show config bar)
  // The gauge fills left-to-right; use a clipped version so the gradient tracks with fill
  const label = pct === 0 ? 'Season just getting started'
    : pct < 20 ? 'Early stages — just warming up'
    : pct < 40 ? 'Getting into it'
    : pct < 60 ? 'Halfway through the season'
    : pct < 80 ? 'In the home stretch'
    : pct < 95 ? 'Almost there'
    : 'Season complete';

  return `
    <div class="dash-season-gauge">
      <div class="dash-season-gauge-header">
        <span class="dash-season-gauge-label">${CURRENT_YEAR} Season Progress</span>
        <span class="dash-season-gauge-pct">${pct}%</span>
      </div>
      <div class="dash-season-track">
        <div class="dash-season-fill" style="width:${pct}%"></div>
      </div>
      <div class="dash-season-sub">${escDash(label)} · ${stats.showsWithPct} show${stats.showsWithPct !== 1 ? 's' : ''} averaged</div>
    </div>
  `;
}

function renderAlertsBar(alerts) {
  if (!alerts.length) return '';
  const items = alerts.map(a =>
    `<div class="dash-alert-line">· <strong>${escDash(a.title)}</strong>${a.body ? ' — ' + escDash(a.body.substring(0, 80)) : ''}</div>`
  ).join('');
  return `
    <div class="dash-alerts-bar">
      <div class="dash-alerts-tag">David</div>
      <div class="dash-alerts-items">${items}</div>
    </div>
  `;
}

function renderStatsRow(s) {
  const outAmt = s.outstandingAmt > 0 ? '$' + (s.outstandingAmt / 100).toLocaleString() : '$0';
  const outClass = s.outstandingAmt > 0 ? 'warn' : '';
  const contractRatio = s.totalActive > 0
    ? `${s.contractSigned} / ${s.totalActive}`
    : '—';
  const contractClass = s.contractSigned < s.totalActive ? 'warn' : 'good';
  const delivClass = s.deliveriesSoon > 0 ? 'info' : '';
  const nextDel = s.nextDelivery
    ? `Next: ${escDash(s.nextDelivery.name)} · ${formatShortDate(s.nextDelivery.date)}`
    : 'None in 45 days';

  const fmtMoney = cents => '$' + (cents / 100).toLocaleString();
  const revTotal = s.marchingRevCents > 0 ? fmtMoney(s.marchingRevCents) : '—';
  const revPaid  = s.marchingPaidCents > 0 ? fmtMoney(s.marchingPaidCents) + ' paid' : null;
  const revOwed  = (s.marchingRevCents - s.marchingPaidCents) > 0
    ? fmtMoney(s.marchingRevCents - s.marchingPaidCents) + ' owed'
    : null;

  return `
    <div class="dash-stats-row">
      <div class="dash-stat">
        <div class="dash-stat-label">Active Shows</div>
        <div class="dash-stat-value">${s.active}</div>
        <div class="dash-stat-sub">${CURRENT_YEAR} season</div>
      </div>
      <div class="dash-stat good">
        <div class="dash-stat-label">Marching Revenue</div>
        <div class="dash-stat-value" style="color:#2ecc71">${revTotal}</div>
        <div class="dash-stat-sub">${[revPaid, revOwed].filter(Boolean).join(' · ') || 'No fees on file'}</div>
      </div>
      <div class="dash-stat ${outClass}">
        <div class="dash-stat-label">Outstanding Invoices</div>
        <div class="dash-stat-value">${outAmt}</div>
        <div class="dash-stat-sub">${s.outstandingCount} invoice${s.outstandingCount !== 1 ? 's' : ''}${s.outstandingOldest > 0 ? ' · oldest ' + s.outstandingOldest + 'd' : ''}</div>
      </div>
      <div class="dash-stat ${contractClass}">
        <div class="dash-stat-label">Contracts Signed</div>
        <div class="dash-stat-value">${contractRatio}</div>
        <div class="dash-stat-sub">${s.totalActive - s.contractSigned > 0 ? (s.totalActive - s.contractSigned) + ' awaiting' : 'All signed'}</div>
      </div>
      <div class="dash-stat ${delivClass}">
        <div class="dash-stat-label">Deliveries ≤ 45 Days</div>
        <div class="dash-stat-value">${s.deliveriesSoon}</div>
        <div class="dash-stat-sub">${nextDel}</div>
      </div>
    </div>
  `;
}

function renderClientSection(clients, yearShows) {
  const marching = clients.filter(c => c.ensemble_type === 'marching' || c.ensemble_type === 'university_band' || c.ensemble_type === 'drum_corps');
  const comp = clients.filter(c => c.ensemble_type === 'composition' ||
    yearShows.some(s => s.client_id === c.id && s.show_type === 'composition'));
  const all = clients;

  return `
    <div class="dash-section-row">
      <div class="dash-section-label">Active Clients</div>
      <div class="dash-filter-row">
        <button class="dash-filter-chip active" data-filter="all">All (${all.length})</button>
        <button class="dash-filter-chip" data-filter="marching">Marching (${marching.length})</button>
        <button class="dash-filter-chip" data-filter="composition">Composition (${comp.length})</button>
        <button class="dash-filter-chip" data-filter="attention">⚠ Needs Attention</button>
      </div>
    </div>
    <div class="dash-client-grid" id="dash-grid">
      ${all.length ? all.map(c => renderClientCard(c, yearShows)).join('') : '<div class="dash-empty">No active clients for ' + CURRENT_YEAR + '</div>'}
    </div>
  `;
}

function renderClientCard(client, yearShows) {
  const show = yearShows.filter(s => s.client_id === client.id)
    .sort((a, b) => (b.id || 0) - (a.id || 0))[0];

  const type = client.ensemble_type || 'composition';
  const isMarching = type === 'marching' || type === 'university_band' || type === 'drum_corps';
  const typeLabel = isMarching ? (type === 'university_band' ? 'University' : type === 'drum_corps' ? 'Drum Corps' : 'Marching') : 'Composition';
  const badgeClass = isMarching ? (type === 'university_band' ? 'badge-university' : type === 'drum_corps' ? 'badge-drum_corps' : 'badge-marching') : 'badge-composition';
  const cardClass = `type-${type === 'university_band' ? 'university' : type === 'drum_corps' ? 'drum_corps' : type === 'marching' ? 'marching' : 'composition'}`;

  const name = client.org_name || client.name || '—';
  const dirName = client.name || '';
  const showTitle = show?.title || '';
  const pct = parseInt(show?.percent_complete) || 0;
  const fillClass = isMarching ? 'v4-fill-pink' : 'v4-fill-cyan';
  const fillColor = pct >= 70 ? 'v4-fill-green' : pct >= 40 ? (isMarching ? 'v4-fill-pink' : 'v4-fill-cyan') : 'v4-fill-amber';

  const contractOk = show?.contract_signed;
  const invoiceOk  = show?.paid ? true : show?.invoiced ? null : false;
  const driveOk    = !!(show?.drive_folder_id);

  const contractDot   = contractOk ? 'v4-dot-green' : 'v4-dot-amber';
  const contractClass = contractOk ? 'ok' : 'warn';
  const invoiceDot    = invoiceOk === true ? 'v4-dot-green' : invoiceOk === null ? 'v4-dot-amber' : 'v4-dot-dim';
  const invoiceClass  = invoiceOk === true ? 'ok' : invoiceOk === null ? 'warn' : '';
  const invoiceLabel  = show?.paid ? 'Paid' : show?.invoiced ? 'Invoiced' : 'Invoice';
  const driveDot      = driveOk ? 'v4-dot-green' : 'v4-dot-dim';
  const driveClass    = driveOk ? 'ok' : '';

  const dueDate = show?.deliver_by_date || show?.due_date;
  let dueEl = '';
  if (dueDate) {
    const days = (new Date(dueDate) - Date.now()) / 86400000;
    const soon = days >= 0 && days <= 45;
    dueEl = `<div class="v4-card-due${soon ? ' soon' : ''}">${formatShortDate(dueDate)}</div>`;
  }

  return `
    <div class="v4-client-card ${cardClass}" data-client-id="${client.id}">
      <div class="v4-card-top">
        <div>
          <div class="v4-card-name">${escDash(name)}</div>
          <div class="v4-card-meta">${CURRENT_YEAR}${dirName && dirName !== name ? ' · ' + escDash(dirName) : ''}</div>
        </div>
        <div class="v4-card-type-badge ${badgeClass}">${typeLabel}</div>
      </div>
      ${showTitle ? `<div class="v4-card-show">"${escDash(showTitle)}"</div>` : ''}
      ${show ? `
      <div class="v4-card-progress">
        <div class="v4-prog-row">
          <span class="v4-prog-label">Progress</span>
          <span class="v4-prog-pct">${pct}%</span>
        </div>
        <div class="v4-prog-track">
          <div class="v4-prog-fill ${fillColor}" style="width:${pct}%"></div>
        </div>
      </div>` : ''}
      <div class="v4-card-status">
        <div class="v4-status-pill">
          <div class="v4-status-dot ${contractDot}"></div>
          <span class="v4-status-text ${contractClass}">Contract</span>
        </div>
        <div class="v4-status-pill">
          <div class="v4-status-dot ${invoiceDot}"></div>
          <span class="v4-status-text ${invoiceClass}">${invoiceLabel}</span>
        </div>
        <div class="v4-status-pill">
          <div class="v4-status-dot ${driveDot}"></div>
          <span class="v4-status-text ${driveClass}">Drive</span>
        </div>
        ${dueEl}
      </div>
    </div>
  `;
}

// ── Filter binding ─────────────────────────────────────────────────────────

function bindFilters(yearClients, yearShows) {
  document.querySelectorAll('.dash-filter-chip').forEach(chip => {
    chip.addEventListener('click', () => {
      document.querySelectorAll('.dash-filter-chip').forEach(c => c.classList.remove('active'));
      chip.classList.add('active');
      filterGrid(chip.dataset.filter, yearClients, yearShows);
    });
  });

  // Open client on card click
  document.querySelectorAll('.v4-client-card').forEach(card => {
    card.addEventListener('click', () => {
      const id = parseInt(card.dataset.clientId);
      if (id && window.openClient) window.openClient(id);
    });
  });
}

function filterGrid(filter, yearClients, yearShows) {
  const grid = document.getElementById('dash-grid');
  if (!grid) return;

  let filtered = yearClients;
  if (filter === 'marching') {
    filtered = yearClients.filter(c => c.ensemble_type === 'marching' || c.ensemble_type === 'university_band' || c.ensemble_type === 'drum_corps');
  } else if (filter === 'composition') {
    filtered = yearClients.filter(c => c.ensemble_type === 'composition' ||
      yearShows.some(s => s.client_id === c.id && s.show_type === 'composition'));
  } else if (filter === 'attention') {
    const now = Date.now();
    filtered = yearClients.filter(c => {
      const show = yearShows.filter(s => s.client_id === c.id).sort((a, b) => b.id - a.id)[0];
      if (!show) return true;
      const invoiceIssue = show.invoiced && !show.paid;
      const noContract = !show.contract_signed;
      const dueDate = show.deliver_by_date || show.due_date;
      const deadlineSoon = dueDate && (new Date(dueDate) - now) / 86400000 <= 30;
      return invoiceIssue || noContract || deadlineSoon;
    });
  }

  grid.innerHTML = filtered.length
    ? filtered.map(c => renderClientCard(c, yearShows)).join('')
    : '<div class="dash-empty">No clients match this filter</div>';

  // Re-bind click handlers
  grid.querySelectorAll('.v4-client-card').forEach(card => {
    card.addEventListener('click', () => {
      const id = parseInt(card.dataset.clientId);
      if (id && window.openClient) window.openClient(id);
    });
  });
}

// ── Past Clients Section ───────────────────────────────────────────────────

function renderPastClientsSection(pastClients, allShows) {
  if (!pastClients || pastClients.length === 0) return '';

  const rows = pastClients.map(c => {
    const clientShows = allShows.filter(s => s.client_id === c.id)
      .sort((a, b) => (b.season_year || 0) - (a.season_year || 0));
    const lastShow = clientShows[0];
    const lastYear = lastShow?.season_year || '—';
    const showCount = clientShows.length;
    const type = c.ensemble_type || 'composition';
    const isMarching = type === 'marching' || type === 'university_band' || type === 'drum_corps';
    const typeLabel = isMarching ? 'Marching' : 'Composition';
    const badgeClass = isMarching ? 'badge-marching' : 'badge-composition';
    const name = c.org_name || c.name || '—';

    return `
      <div class="v4-past-client-row" data-client-id="${c.id}">
        <span class="past-client-name">${escDash(name)}</span>
        <span class="past-client-badge ${badgeClass}">${typeLabel}</span>
        <span class="past-client-meta">Last: ${lastYear} · ${showCount} show${showCount !== 1 ? 's' : ''}</span>
        ${lastShow?.title ? `<span class="past-client-show">"${escDash(lastShow.title)}"</span>` : ''}
      </div>
    `;
  }).join('');

  return `
    <div class="dash-past-section" id="dash-past-section">
      <div class="dash-past-header" id="dash-past-toggle">
        <span class="dash-section-label" style="margin-bottom:0">Past Clients</span>
        <span class="dash-past-count">${pastClients.length}</span>
        <span class="dash-past-chevron" id="dash-past-chevron">▸</span>
      </div>
      <div class="dash-past-list" id="dash-past-list" style="display:none">
        ${rows}
      </div>
    </div>
  `;
}

function bindPastClientsToggle() {
  const toggle = document.getElementById('dash-past-toggle');
  const list   = document.getElementById('dash-past-list');
  const chev   = document.getElementById('dash-past-chevron');
  if (!toggle || !list) return;

  toggle.addEventListener('click', () => {
    const open = list.style.display !== 'none';
    list.style.display = open ? 'none' : 'block';
    if (chev) chev.textContent = open ? '▸' : '▾';
  });

  list.querySelectorAll('.v4-past-client-row').forEach(row => {
    row.addEventListener('click', () => {
      const id = parseInt(row.dataset.clientId);
      if (id && window.openClient) window.openClient(id);
    });
  });
}

function bindNewClientButtons() {
  window.openNewClient = function(type) {
    const typeMap = {
      marching: { field: 'marching', title: 'New Marching Client' },
      composition: { field: 'composition', title: 'New Composition Client' },
      university: { field: 'university_band', title: 'New University / College Band' },
      drum_corps: { field: 'drum_corps', title: 'New Drum Corps' },
    };
    const t = typeMap[type] || typeMap.marching;
    const ensField = document.getElementById('field-ensemble-type');
    const titleEl  = document.getElementById('modal-client-title');
    const form     = document.getElementById('form-new-client');
    const modal    = document.getElementById('modal-new-client');
    if (ensField) ensField.value = t.field;
    if (titleEl) titleEl.textContent = t.title;
    if (form) form.reset();
    if (ensField) ensField.value = t.field;
    if (modal) modal.style.display = 'flex';
  };
}

// ── Helpers ────────────────────────────────────────────────────────────────

async function fetchJSON(path) {
  const r = await fetch(path);
  if (!r.ok) throw new Error(`${path} → ${r.status}`);
  const text = await r.text();
  return text ? JSON.parse(text) : [];
}

function escDash(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function clientName(clients, id) {
  const c = clients.find(c => c.id === id);
  return c ? (c.org_name || c.name || '') : '';
}

function daysSince(dateStr) {
  if (!dateStr) return 0;
  return Math.floor((Date.now() - new Date(dateStr)) / 86400000);
}

function formatShortDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
